import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner, Form, InputGroup, Badge, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { FaSearch, FaFilter, FaTools } from "react-icons/fa";
import Layout from "../../hocs/Layout";
import { resourceService } from "../../services/resourceService";
import { getFileIcon, getFileExtension } from "../../utils/fileUtils";

function Resources({ isSuperAdmin }) {
    const navigate = useNavigate();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");

    const loadResources = async () => {
        try {
            setLoading(true);
            const data = await resourceService.getResources();
            // Exclude legal documents and release notes from the general browser
            setResources(data.filter(res => res.category !== 'legal' && res.category !== 'release_notes'));
        } catch (error) {
            console.error("Error loading resources:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadResources();
    }, []);

    const filteredResources = resources.filter(res => {
        const matchesSearch = res.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (res.description && res.description.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = categoryFilter === "all" || res.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    // Grouping for the UI
    const groupedResources = filteredResources.reduce((acc, resource) => {
        const cat = resource.category || 'otros';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(resource);
        return acc;
    }, {});

    const categoryLabels = {
        electricidad: 'Electricidad',
        gas: 'Gas',
        otros: 'Otros'
    };

    return (
        <Layout>
            <Container className="py-4">
                <div className="mb-5 animate-in d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="display-6 fw-bold text-primary mb-2">Biblioteca de Recursos</h2>
                        <p className="text-muted h5 fw-normal">Accede a tarifarios, guías y documentación actualizada.</p>
                    </div>
                    {isSuperAdmin && (
                        <Button
                            variant="outline-primary"
                            className="rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-2"
                            onClick={() => navigate('/admin/recursos')}
                        >
                            <FaTools />
                            Administrar
                        </Button>
                    )}
                </div>

                {/* Filters */}
                <Card className="shadow-sm border-0 mb-4 animate-in delay-1">
                    <Card.Body className="p-3">
                        <Row className="g-3">
                            <Col md={8}>
                                <InputGroup className="filter-recursos">
                                    <InputGroup.Text className="bg-white border-end-0">
                                        <FaSearch className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Control
                                        placeholder="Buscar por título o descripción..."
                                        className="border-start-0 ps-0"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                            </Col>
                            <Col md={4}>
                                <InputGroup className="filter-recursos">
                                    <InputGroup.Text className="bg-white border-end-0">
                                        <FaFilter className="text-muted" />
                                    </InputGroup.Text>
                                    <Form.Select
                                        className="border-start-0 ps-0"
                                        value={categoryFilter}
                                        onChange={(e) => setCategoryFilter(e.target.value)}
                                    >
                                        <option value="all">Todas las categorías</option>
                                        <option value="electricidad">Electricidad</option>
                                        <option value="gas">Gas</option>
                                        <option value="otros">Otros</option>
                                    </Form.Select>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : filteredResources.length === 0 ? (
                    <div className="text-center py-5 bg-white rounded-4 shadow-sm">
                        <p className="text-muted mb-0">No se encontraron recursos que coincidan con tu búsqueda.</p>
                    </div>
                ) : (
                    Object.entries(groupedResources).map(([category, items], idx) => (
                        <div key={category} className={`mb-5 animate-in delay-${idx + 2}`}>
                            <h4 className="fw-bold text-secondary mb-3 text-uppercase" style={{ fontSize: '0.9rem', letterSpacing: '1px' }}>
                                {categoryLabels[category] || category}
                            </h4>
                            <Row className="g-4">
                                {items.map((item) => {
                                    const { icon: FileIcon, color: iconColor } = getFileIcon(item.file);
                                    const ext = getFileExtension(item.file);
                                    return (
                                        <Col lg={4} md={6} key={item.id}>
                                            <a href={item.file_url} target="_blank" rel="noopener noreferrer" className="text-decoration-none h-100">
                                                <Card className="h-100 border-0 shadow-sm hover-elevate transition-all">
                                                    <Card.Body className="p-4">
                                                        <div className="d-flex align-items-start mb-3">
                                                            <div className={`p-3 bg-light rounded-circle ${iconColor} me-3`}>
                                                                <FileIcon size={24} />
                                                            </div>
                                                            <div style={{ minWidth: 0 }}>
                                                                <h5 className="mb-1 text-dark fw-bold text-truncate-2" style={{ fontSize: '1.1rem' }}>{item.title}</h5>
                                                                <Badge bg="light" text="dark" className="border text-uppercase">{ext || 'Archivo'}</Badge>
                                                            </div>
                                                        </div>
                                                        {item.description && (
                                                            <p className="text-muted small mb-0 text-truncate-3">
                                                                {item.description}
                                                            </p>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            </a>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    ))
                )}
            </Container>

            <style>{`
                .text-truncate-2 {
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
                .text-truncate-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }
            `}</style>
        </Layout>
    );
}

const mapStateToProps = (state) => ({
    isSuperAdmin: state.Auth ? state.Auth.isSuperAdmin : false
});

export default connect(mapStateToProps)(Resources);
